import {
    Component, DestroyRef, inject, Input, OnInit, ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import {
    combineLatest,
    filter, firstValueFrom, forkJoin, from,
    startWith,
    timer,
} from 'rxjs';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { ApiService } from 'src/app/services/api.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { SentryService } from 'src/app/services/sentry.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UserService } from 'src/app/services/user.service';
import {
    ApolloService, Documents_Order_By, getCollection_collections_by_pk, Order_By,
} from 'src/gql-generated/generated';
import { ConnectionService } from 'src/app/services/connection.service';
import { DynamicDialogComponent } from '../../shared/dynamic-dialog/dynamic-dialog.component';
import { FileListComponent } from '../../shared/file-list/file-list.component';
import { NgIf, NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { FileSelectorComponent } from '../../shared/file-selector/file-selector.component';
import { ExpansionContentComponent } from '../../shared/expansion-content/expansion-content.component';

@Component({
    selector: 'app-collection-edit',
    templateUrl: './collection-edit.component.html',
    styleUrl: './collection-edit.component.scss',
    imports: [
        NgIf,
        MatIcon,
        MatButton,
        MatIconButton,
        RouterLink,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatError,
        CdkTextareaAutosize,
        MatRadioGroup,
        MatRadioButton,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        NgClass,
        FileSelectorComponent,
        FileListComponent,
        ExpansionContentComponent,
        DynamicDialogComponent,
    ],
})
export class CollectionEditComponent implements OnInit {
    /**
     * Reference to wait dialog
     */
    @ViewChild('waitDialog') waitDialog?: DynamicDialogComponent;

    /**
     * Reference to file list
     */
    @ViewChild('fileList') fileList?: FileListComponent;

    /**
     * True if this will be the first collection
     */
    @Input() firstCollection = false;

    /**
     * Formgroup tracking fields that make up a valid collection
     */
    collectionFormGroup = new FormGroup({
        name: new FormControl<string>('', Validators.required),
        description: new FormControl<string>('', Validators.required),
        files: new FormControl<{
            file: File;
            id?: string;
            status?: 'available' | 'processing' | 'failed' | 'queued' | 'retry';
            subText?: string;
            created_at: Date;
        }[]>(
            [],
            { validators: [], nonNullable: true },
        ),
        systemPrompt: new FormControl<string>(''),
        temperature: new FormControl<number | undefined>(0.2, [
            (control): null | { range: true; } => {
                if (!control.value && control.value !== 0) {
                    return null;
                }
                return (control.value < 0 || control.value > 1) ? { range: true } : null;
            },
        ]),
        private: new FormControl(false),
    });

    /**
     * Latest value of current collection. Only has a value when component is used for editing.
     */
    collection?: getCollection_collections_by_pk;

    /**
     * Tracks whether this component is used to create a new collection or edit an existing one
     */
    mode: 'new' | 'edit' = window.location.href.split('/').at(-1) === 'edit' ? 'edit' : 'new';

    /**
     * Show warning that collection contains failed documents
     */
    showFailedDocumentsWarning = false;

    /**
     * True when awaiting an async operation. Used to prevent the user from (accidentally) running a function twice.
     */
    awaitingAsyncOperation = false;

    /**
     * Documents order by statemtent currently in use
     */
    currentDocumentSort: Documents_Order_By = { created_at: Order_By.Desc };

    /**
     * Destroy ref used in takeUntilDestroyed
     */
    private destroyRef = inject(DestroyRef);

    constructor(
        private apollo: ApolloService,
        private user: UserService,
        private api: ApiService,
        private snackbar: SnackbarService,
        private route: ActivatedRoute,
        private router: Router,
        public breakpoint: BreakpointService,
        public organization: OrganizationService,
        private sentry: SentryService,
        public connection: ConnectionService,
    ) {
        // Only load default system prompt is organization has access to its setting
        if (this.mode === 'new' && this.organization.currentOrganization$.value?.settings?.features.includes('CollectionAdvancedSettings')) {
            this.loadDefaultSystemPrompt();
        }
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            startWith(null),
            takeUntilDestroyed(),
        ).subscribe(() => {
            this.getCollection();
        });
    }

    ngOnInit(): void {
        // Chatters are only allowed on /new page if there are no collections yet
        // this is because otherwise they would click Collections in the nav bar and immediately get redirected
        if (!this.firstCollection && this.connection.role.value === 'chatter') {
            this.router.navigate(['collections']);
        }
    }

    /**
     * Get collection
     */
    async getCollection(): Promise<void> {
        const collectionId = this.route.snapshot.paramMap.get('collectionId') || undefined;
        if (collectionId) {
            const result = await firstValueFrom(this.apollo.getCollection({
                id: collectionId,
                documents_order_by: [this.currentDocumentSort],
            }));

            if (result.data?.collections_by_pk) {
                this.collection = result.data.collections_by_pk;
                // Reflect collection value in collection form group
                this.collectionFormGroup.patchValue({
                    name: this.collection.name,
                    description: this.collection.description,
                    temperature: this.collection.llm_temperature,
                    systemPrompt: this.collection.system_prompt,
                    private: this.collection.private,
                    files: this.collection.Documents.map((document) => ({
                        file: { name: document.File.name } as File,
                        id: document.File.id,
                        status: document.status as 'available' | 'processing' | 'failed' | 'queued' | 'retry' | undefined,
                        created_at: new Date(document.created_at),
                    })),
                });
            }

            // Show failed documents warning
            this.showFailedDocumentsWarning = !!this.collection?.Documents.some((document) => document.status === 'failed');

            // Subscribe to file changes
            this.collectionFormGroup.controls.files.valueChanges.pipe(
                takeUntilDestroyed(this.destroyRef),
            ).subscribe(() => {
                // Mark formgroup as touched if user changes any of the selected files. This is used to make sure a warning dialog is shown
                // if the user tries to leave with unsaved changes
                this.collectionFormGroup.markAsTouched();
                // Hide failed documents warning if no documents are failed
                if (this.showFailedDocumentsWarning) {
                    this.showFailedDocumentsWarning = !!this.collectionFormGroup.value.files?.some((file) => file.status === 'failed');
                }
            });
        }
    }

    /**
     * Attempts to save a collection. First uploads all files (and makes the user wait for this to complete),
     * then stores the data in the hasura database.
     */
    async saveCollection(): Promise<void> {
        if (!this.awaitingAsyncOperation) {
            this.awaitingAsyncOperation = true;
            this.collectionFormGroup.markAllAsTouched();
            if (this.collectionFormGroup.valid && this.organization.currentOrganization$.value?.id) {
                try {
                    let uploadedFiles: { file: File; id: string; }[] = [];
                    // Upload files if they were selected
                    if (this.collectionFormGroup.value.files && this.collectionFormGroup.value.files.length > 0) {
                        // Attempt file upload of all files. Force the user to wait at least 2 seconds
                        this.waitDialog?.open();
                        const uploadObservables = this.collectionFormGroup.value.files.map((file) => from(this.api.uploadFile(file.file)));
                        const [filesUploadResult] = await firstValueFrom(combineLatest([forkJoin(uploadObservables), timer(2000)]));
                        // Store the autogenerated file id, which is needed in the insertCollection apollo mutation
                        uploadedFiles = this.collectionFormGroup.value.files.map((file, index) => ({
                            file: file.file,
                            id: filesUploadResult[index].data.file_id,
                        }));
                        // Set organization_id for newly generated files
                        if (uploadedFiles.length > 0) {
                            await firstValueFrom(this.apollo.updateFiles({
                                fileIds: uploadedFiles.map((file) => file.id),
                                fileUpdates: { organization_id: this.organization.currentOrganization$.value.id },
                            }));
                        }
                        this.waitDialog?.close();
                    }

                    // Attempt collection insert
                    const insertCollectionResult = await firstValueFrom(this.apollo.insertCollection({
                        collection: {
                            name: this.collectionFormGroup.value.name,
                            description: this.collectionFormGroup.value.description,
                            created_by_id: this.user.profile.id,
                            organization_id: this.organization.currentOrganization$.value.id,
                            private: this.collectionFormGroup.value.private,
                            Documents: {
                                data: uploadedFiles.map((file) => ({
                                    created_by_id: this.user.profile.id,
                                    status: 'processing',
                                    file_id: file.id,
                                })),
                            },
                            // Only add fields below if they have a value. This prevents overwriting the default
                            ...(this.collectionFormGroup.value.temperature && { llm_temperature: this.collectionFormGroup.value.temperature }),
                            ...(this.collectionFormGroup.value.systemPrompt && { system_prompt: this.collectionFormGroup.value.systemPrompt }),
                        },
                    }));

                    // If successful, navigate the user to the detail page
                    if (insertCollectionResult.data?.insert_collections_one?.id) {
                        this.snackbar.success($localize`Collectie succesvol aangemaakt`);
                        this.collectionFormGroup.reset();
                        this.router.navigate(['collections', insertCollectionResult.data.insert_collections_one.id]);
                    }
                } catch (error) {
                    this.sentry.error(error);
                    this.waitDialog?.close();
                    this.snackbar.error($localize`Collectie aanmaken mislukt`);
                }
            }
            this.awaitingAsyncOperation = false;
        }
    }

    /**
     * Attempt to update a collection. First the new files are uploaded (user is forced to wait for this to complete), then the
     * removed files are deleted from S3 storage via the backend and finally the required database mutations are made.
     */
    async updateCollection(): Promise<void> {
        if (!this.awaitingAsyncOperation) {
            this.awaitingAsyncOperation = true;
            this.collectionFormGroup.markAllAsTouched();
            if (this.collectionFormGroup.valid && this.collection) {
                try {
                    // Attempt file upload of all new files. Force the user to wait at least 2 seconds
                    const newFiles = this.collectionFormGroup.value.files!
                        .filter((file) => !this.collection?.Documents.some((document) => document.File.id === file.id));
                    if (newFiles.length) {
                        this.waitDialog?.open();
                        const uploadObservables = newFiles.map((file) => from(this.api.uploadFile(file.file)));
                        const [filesUploadResult] = await firstValueFrom(combineLatest([forkJoin(uploadObservables), timer(2000)]));
                        // Store the autogenerated file id
                        filesUploadResult.forEach((result, index) => {
                            newFiles[index].id = result.data.file_id;
                        });
                        // Set organization_id for newly generated files
                        await firstValueFrom(this.apollo.updateFiles({
                            fileIds: newFiles.map((file) => file.id!),
                            fileUpdates: { organization_id: this.organization.currentOrganization$.value?.id },
                        }));
                        this.waitDialog?.close();
                    }

                    // Find removed files and remove them from storage
                    const removedFiles = this.collection.Documents
                        .filter((document) => !this.collectionFormGroup.value.files?.some((file) => file.id === document.File.id));
                    removedFiles.forEach((file) => this.api.deleteFile(file.File.id));

                    // Find retry files
                    const retryFiles = this.collectionFormGroup.value.files!.filter((file) => file.status === 'retry');

                    // Attempt update
                    const updateCollectionResult = await firstValueFrom(this.apollo.updateCollection({
                        id: this.collection.id,
                        collectionUpdates: {
                            name: this.collectionFormGroup.value.name,
                            description: this.collectionFormGroup.value.description,
                            updated_by_id: this.user.profile.id,
                            llm_temperature: this.collectionFormGroup.value.temperature,
                            system_prompt: this.collectionFormGroup.value.systemPrompt,
                            private: this.collectionFormGroup.value.private,
                        },
                        updatedById: this.user.profile.id,
                        // Soft delete removed and retry files
                        deletedFileIds: removedFiles.map((file) => file.File.id).concat(retryFiles.map((file) => file.id!)),
                        // Add new documents and retry files
                        newDocuments: newFiles.concat(retryFiles).map((file) => ({
                            created_by_id: this.user.profile.id,
                            collection_id: this.collection?.id,
                            file_id: file.id,
                            status: 'processing',
                        })),
                    }));

                    // If update is successful, redirect the user to the detail page.
                    if (updateCollectionResult.data?.update_collections_by_pk?.id) {
                        this.snackbar.success($localize`Collectie succesvol aangepast`);
                        this.collectionFormGroup.reset();
                        this.router.navigate(['collections', this.collection.id]);
                    }
                } catch (error) {
                    this.sentry.error(error);
                    this.waitDialog?.close();
                    this.snackbar.error($localize`Collectie aanpassen mislukt`);
                }
            }
            this.awaitingAsyncOperation = false;
        }
    }

    /**
     * Fetches the default system prompt and sets it as the form value
     * if the user has not already entered a value or is not editing
     * an existing collection.
    */
    async loadDefaultSystemPrompt(): Promise<void> {
        try {
            const result = await firstValueFrom(this.apollo.getDefaultSystemPrompt());
            const raw = result.data?.get_default_system_prompt[0]?.system_prompt || '';
            // Hasura adds ::text to default value of columns
            const systemPrompt = raw.replace(/^'|'::text$/g, '');
            this.collectionFormGroup.controls.systemPrompt.setValue(systemPrompt);
        } catch (error) {
            this.sentry.error(error);
        }
    }

    /**
     * Changes status of failed documents to retry
     */
    reselectFailedDocuments(): void {
        const failedDocuments = this.collectionFormGroup.value.files?.filter((file) => file.status === 'failed');
        failedDocuments?.forEach((document) => {
            document.status = 'retry';
        });
        this.showFailedDocumentsWarning = false;
    }
}
